class CartPerksProgress extends HTMLElement {
  async connectedCallback() {
    this.#cacheElements();
    this.#parseTiers();
    this.#qualifyingIds = this.#parseIdSet(this.dataset.qualifyingIds);
    this.#matchAll = this.dataset.matchAll !== 'false';
    await this.#refreshFromCart();
    document.addEventListener('cart:update', this.#handleCartUpdate);
    document.addEventListener('cart:updated', this.#handleCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:update', this.#handleCartUpdate);
    document.removeEventListener('cart:updated', this.#handleCartUpdate);
  }

  #elements;
  #tiers = [];
  #qualifyingIds = new Set();
  #matchAll = true;

  #handleCartUpdate = async () => {
    await this.#refreshFromCart();
  };

  #cacheElements() {
    this.#elements = {
      message: this.querySelector('[data-message]'),
      progressBar: this.querySelector('[data-progress-bar]'),
      markers: Array.from(this.querySelectorAll('[data-tier-marker]')),
    };
  }

  #parseTiers() {
    try {
      this.#tiers = JSON.parse(this.dataset.tiers || '[]');
    } catch {
      this.#tiers = [];
    }
  }

  async #refreshFromCart() {
    try {
      const response = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!response.ok) return;
      const cart = await response.json();
      this.#render(cart);
    } catch {
      // noop: keep last rendered state
    }
  }

  #getQualifyingTotals(cart) {
    if (this.#matchAll) {
      // Money tiers use the pre-discount total so discounts (e.g. 40% off) don't
      // push customers back below a tier they already reached.
      const preDiscountTotal =
        this.#parseInteger(cart?.original_total_price, 0) ||
        this.#parseInteger(cart?.total_price, 0) + this.#parseInteger(cart?.total_discount, 0);
      return {
        money: Math.max(0, preDiscountTotal),
        items: Math.max(0, this.#parseInteger(cart?.item_count, 0)),
      };
    }

    const items = Array.isArray(cart?.items) ? cart.items : [];
    let money = 0;
    let itemCount = 0;

    for (const item of items) {
      const productId = this.#parseInteger(item?.product_id, 0);
      if (!productId || !this.#qualifyingIds.has(productId)) continue;

      const quantity = this.#parseInteger(item?.quantity, 0);
      if (quantity <= 0) continue;

      itemCount += quantity;
      const lineMoney =
        this.#parseInteger(item?.original_line_price, 0) ||
        this.#parseInteger(item?.original_price, 0) * quantity ||
        this.#parseInteger(item?.line_price, 0);
      money += Math.max(0, lineMoney);
    }

    return { money, items: itemCount };
  }

  #render(cart) {
    if (this.#tiers.length === 0) return;

    const { money: currentMoney, items: currentItems } = this.#getQualifyingTotals(cart);
    const tierCount = this.#tiers.length;
    const segmentPercent = 100 / tierCount;

    let firstLockedIndex = -1;
    this.#tiers.forEach((tier, index) => {
      const current = tier.type === 'money' ? currentMoney : currentItems;
      const isUnlocked = current >= tier.threshold;
      if (!isUnlocked && firstLockedIndex === -1) firstLockedIndex = index;
      this.#elements.markers[index]?.classList.toggle('is-unlocked', isUnlocked);
    });

    let progressPercent = 100;
    let message;

    if (firstLockedIndex === -1) {
      message = this.#tiers[tierCount - 1].unlockedMessage;
    } else {
      const tier = this.#tiers[firstLockedIndex];
      const current = tier.type === 'money' ? currentMoney : currentItems;
      const remainingRaw = Math.max(0, tier.threshold - current);
      const remaining = tier.type === 'money' ? this.#formatMoney(remainingRaw) : String(remainingRaw);

      progressPercent = Math.round(
        segmentPercent * firstLockedIndex + segmentPercent * Math.min(1, current / tier.threshold)
      );
      message = (tier.progressMessage || '').replace('{remaining}', remaining);
    }

    this.style.setProperty('--cart-perks-progress-value', `${progressPercent}%`);

    if (this.#elements.progressBar) {
      this.#elements.progressBar.setAttribute('aria-valuenow', String(progressPercent));
      this.#elements.progressBar.setAttribute('aria-label', this.dataset.progressLabel || 'Progress toward cart perks');
    }

    if (this.#elements.message && message) {
      this.#elements.message.textContent = message;
    }
  }

  #formatMoney(cents) {
    const format = this.dataset.moneyFormat || '${{amount}}';
    const value = cents / 100;

    const withDelimiters = (number, decimals, thousands = ',', decimal = '.') => {
      const parts = number.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(decimal);
    };

    const formatted = format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, placeholder) => {
      switch (placeholder) {
        case 'amount':
          return withDelimiters(value, 2);
        case 'amount_no_decimals':
          return withDelimiters(Math.ceil(value), 0);
        case 'amount_with_comma_separator':
          return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDelimiters(Math.ceil(value), 0, '.');
        case 'amount_with_apostrophe_separator':
          return withDelimiters(value, 2, "'");
        default:
          return match;
      }
    });

    // The money format may contain markup (e.g. <span class=money>); strip tags
    // because the value is inserted as plain text.
    return formatted.replace(/<[^>]*>/g, '').trim();
  }

  #parseIdSet(value) {
    if (!value) return new Set();
    return new Set(
      value
        .split(',')
        .map((entry) => Number.parseInt(entry, 10))
        .filter((entry) => Number.isFinite(entry) && entry > 0)
    );
  }

  #parseInteger(value, fallback) {
    const parsed = Number.parseInt(value ?? '', 10);
    if (Number.isNaN(parsed)) return fallback;
    return parsed;
  }
}

if (!customElements.get('cart-perks-progress')) {
  customElements.define('cart-perks-progress', CartPerksProgress);
}
